<script setup lang="ts">
    import { Minus } from 'lucide-vue-next';
    import { Button } from '@/components/ui/button';
    import {
        AlertDialog,
        AlertDialogTrigger,
        AlertDialogContent,
        AlertDialogHeader,
        AlertDialogTitle,
        AlertDialogDescription,
        AlertDialogFooter,
        AlertDialogCancel,
        AlertDialogAction,
    } from '@/components/ui/alert-dialog';
    import { toast } from 'vue-sonner';
    import { useI18n } from 'vue-i18n';
    import { useMutation } from '@tanstack/vue-query';
    import { usePage } from '@inertiajs/vue3';
    import { useQueryClient } from '@tanstack/vue-query';
    import axios from 'axios';
    import QueryKey from '@/enums/QueryKey';
    import type { SharedData } from '@/types';
    import { computed } from 'vue';

    const queryClient = useQueryClient();

    const page = usePage<SharedData>();
    const companyId = computed(() => page.props.auth.user.selected_company_id);

    const { t } = useI18n();

    const { mutate } = useMutation({
        mutationFn: (formData: { company_id: number }) =>
            axios.post(route('v2.voucher.admin.clear-list'), formData),
        onSuccess: (data) => {
            queryClient.invalidateQueries({ queryKey: [QueryKey.VOUCHERS, companyId.value] });

            toast.success(data.data.success);
        },
        onError: (error) => {
            toast.error(t('forms.system_error'));
        },
    });

    function clearList() {
        mutate({
            company_id: companyId.value,
        });
    }
</script>

<template>
    <AlertDialog>
        <AlertDialogTrigger as-child>
            <Button
                variant="ghost_destructive"
                width="narrow"
                class="self-start"
                size="xs"
            >
                {{ $t('vouchers.clear_list') }}
                <Minus class="text-destructive" />
            </Button>
        </AlertDialogTrigger>
        <AlertDialogContent>
            <AlertDialogHeader>
                <AlertDialogTitle>{{ t('vouchers.js.clear_list_title') }}</AlertDialogTitle>
                <AlertDialogDescription>
                    {{ t('vouchers.js.clear_list') }}
                </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
                <AlertDialogCancel>{{ t('forms.no') }}</AlertDialogCancel>
                <AlertDialogAction
                    variant="default"
                    @click="clearList"
                >
                    {{ t('forms.yes') }}
                </AlertDialogAction>
            </AlertDialogFooter>
        </AlertDialogContent>
    </AlertDialog>
</template>
